import { useState, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WhatsappLogo, ArrowRight, Shield, Storefront } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, loginMfa, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const roleParam = params.get("role"); // admin | revenda | cliente
  const initialRole = roleParam === "admin" ? "master" : (roleParam === "revenda" ? "reseller" : "empresa");
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => { setRole(initialRole); }, [initialRole]);

  const roleHome = (u) => u.role === "master" ? "/master" : (u.role === "reseller_owner" ? "/reseller" : "/kanban");

  if (user) return <Navigate to={roleHome(user)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email.trim().toLowerCase(), password);
      if (result && result.mfaRequired) {
        setMfaStep(true);
        setSubmitting(false);
        return;
      }
      toast.success(`Bem-vindo, ${result.name}`);
      navigate(roleHome(result), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await loginMfa(email.trim().toLowerCase(), password, mfaCode);
      toast.success(`Bem-vindo, ${u.name}`);
      navigate(roleHome(u), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr]">
      {/* LEFT — Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 bg-[#0F4C3A] text-[#F7F7F5] overflow-hidden">
        <div className="absolute inset-0 tick-grain opacity-40" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-10 w-10 bg-[#F7F7F5] text-[#0F4C3A] flex items-center justify-center">
            <WhatsappLogo size={22} weight="bold" />
          </div>
          <span className="font-bold tracking-tight text-lg">NETSZAP</span>
        </div>

        <div className="relative z-10 space-y-6 max-w-lg">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#22A882]">Plataforma B2B</p>
          <h1 className="text-4xl lg:text-5xl font-black leading-[1.05] tracking-tighter">
            Centralize o WhatsApp da sua operação.
          </h1>
          <p className="text-[#c8d5cf] leading-relaxed">
            Kanban de tickets, chat em tempo real, gestão de planos e revendedores — tudo num só painel, feito para quem opera atendimento em escala.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
            <div><div className="text-2xl font-bold">3</div><div className="text-xs text-[#a3b5ac] mt-1">Painéis</div></div>
            <div><div className="text-2xl font-bold">∞</div><div className="text-xs text-[#a3b5ac] mt-1">Atendentes</div></div>
            <div><div className="text-2xl font-bold">24/7</div><div className="text-xs text-[#a3b5ac] mt-1">Operação</div></div>
          </div>
        </div>

        <div className="relative z-10 text-xs text-[#a3b5ac]">
          © {new Date().getFullYear()} NetsZap — feito no Brasil
        </div>
      </div>

      {/* RIGHT — Form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[#F7F7F5]">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Entrar</p>
            <h2 className="text-3xl font-bold tracking-tight text-[#1A1A1A] mt-2">Acesse seu painel</h2>
            <p className="text-sm text-[#5C5C59] mt-2">Selecione seu perfil e faça login.</p>
          </div>

          {/* Role selector */}
          <div className="grid grid-cols-3 gap-2 mb-6" role="tablist">
            <button
              type="button"
              onClick={() => setRole("master")}
              data-testid="role-master-btn"
              className={`flex flex-col items-center gap-1 p-3 border text-center transition-colors duration-200 ${role === "master" ? "border-[#0F4C3A] bg-white shadow-brutal" : "border-[#E5E5E0] bg-white/60 hover:bg-white"}`}
            >
              <Shield size={20} weight="duotone" className="text-[#0F4C3A]" />
              <div className="text-xs font-semibold">Administrador</div>
            </button>
            <button
              type="button"
              onClick={() => setRole("reseller")}
              data-testid="role-reseller-btn"
              className={`flex flex-col items-center gap-1 p-3 border text-center transition-colors duration-200 ${role === "reseller" ? "border-[#0F4C3A] bg-white shadow-brutal" : "border-[#E5E5E0] bg-white/60 hover:bg-white"}`}
            >
              <Storefront size={20} weight="duotone" className="text-[#8B5CF6]" />
              <div className="text-xs font-semibold">Revenda</div>
            </button>
            <button
              type="button"
              onClick={() => setRole("empresa")}
              data-testid="role-company-btn"
              className={`flex flex-col items-center gap-1 p-3 border text-center transition-colors duration-200 ${role === "empresa" ? "border-[#0F4C3A] bg-white shadow-brutal" : "border-[#E5E5E0] bg-white/60 hover:bg-white"}`}
            >
              <Storefront size={20} weight="duotone" className="text-[#2563EB]" />
              <div className="text-xs font-semibold">Cliente</div>
            </button>
          </div>

          <form onSubmit={mfaStep ? submitMfa : onSubmit} className="space-y-4" data-testid="login-form">
            {mfaStep ? (
              <>
                <div className="bg-[#EDF3F0] border border-[#0F4C3A] p-3 text-xs">
                  🔒 <b>Autenticação de 2 fatores</b><br />
                  Digite o código do Google Authenticator para {email}
                </div>
                <div>
                  <Label htmlFor="mfaCode" className="text-xs uppercase tracking-wider font-semibold">Código (6 dígitos)</Label>
                  <Input
                    id="mfaCode" autoFocus autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required data-testid="login-mfa-input"
                    className="mt-1 h-11 bg-white border-[#E5E5E0] rounded-none focus-visible:ring-[#0F4C3A] text-center text-xl tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <Button
                  type="submit" disabled={submitting || mfaCode.length < 6}
                  data-testid="login-mfa-submit"
                  className="w-full h-11 bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none font-semibold tracking-wide"
                >
                  {submitting ? "Verificando…" : "Entrar"}
                  <ArrowRight size={18} weight="bold" className="ml-2" />
                </Button>
                <button
                  type="button"
                  onClick={() => { setMfaStep(false); setMfaCode(""); }}
                  className="text-xs text-[#5C5C59] underline w-full text-center"
                >
                  ← Voltar
                </button>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold">E-mail</Label>
                  <Input
                    id="email" type="email" autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    required data-testid="login-email-input"
                    className="mt-1 h-11 bg-white border-[#E5E5E0] rounded-none focus-visible:ring-[#0F4C3A]"
                    placeholder="voce@empresa.com"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold">Senha</Label>
                    <a
                      href="/forgot-password"
                      className="text-xs text-[#0F4C3A] hover:underline font-semibold"
                      data-testid="login-forgot-password"
                    >
                      Esqueci minha senha
                    </a>
                  </div>
                  <Input
                    id="password" type="password" autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required data-testid="login-password-input"
                    className="mt-1 h-11 bg-white border-[#E5E5E0] rounded-none focus-visible:ring-[#0F4C3A]"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  type="submit" disabled={submitting}
                  data-testid="login-submit-btn"
                  className="w-full h-11 bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none font-semibold tracking-wide transition-colors duration-200 shadow-brutal"
                >
                  {submitting ? "Entrando…" : "Entrar"}
                  <ArrowRight size={18} weight="bold" className="ml-2" />
                </Button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
